import { Ref, forwardRef, useImperativeHandle, useState } from "react";
import { WalletRead, MyWalletService, WalletUpdate } from "@/domains/wallet";
// components
import { Button, Form, Input, Modal } from "antd";
import { t } from "i18next";

interface UpdateModalProps {
  onUpdate: () => void;
}

export interface UpdateModalRef {
  open: (wallet: WalletRead) => void;
}

export const UpdateModal = forwardRef(
  ({ onUpdate }: UpdateModalProps, ref: Ref<UpdateModalRef>) => {
    const [showing, setShowing] = useState(false);

    const [wallet, setWallet] = useState<WalletRead>();

    const [form] = Form.useForm<WalletUpdate>();

    const handleConfirm = async () => {
      if (!wallet) return;

      const formValue = await form.validateFields();

      try {
        await MyWalletService.update(wallet.id, formValue).run();
        setShowing(false);
        onUpdate();
      } catch (e) {
        console.log(e);
      }
    };

    const handleCancel = () => {
      setShowing(false);
    };

    useImperativeHandle(
      ref,
      () => {
        const open = (_wallet: any) => {
          form.resetFields();

          form.setFieldsValue({
            daily_max: _wallet.daily_max,
            weekly_max: _wallet.weekly_deposit,
            monthly_max: _wallet.monthly_deposit,
            balance_max: _wallet.balance_max,
            note: "",
            status: _wallet.status,
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setWallet(_wallet);

          setShowing(true);
        };

        return { open };
      },
      [form]
    );

    return (
      <Modal
        title={t("domains.Wallet.updateWallet")}
        open={showing}
        footer={[
          <div className="flex jc:end gap:8" key="footer">
            <Button key="back" onClick={handleCancel}>
              {t("systems.Action.cancel")}
            </Button>
            <Button key="submit" type="primary" onClick={handleConfirm}>
              {t("systems.Action.submit")}
            </Button>
          </div>,
        ]}
        onCancel={handleCancel}
      >
        <Form className="mt:16" form={form} labelCol={{ span: 6 }}>
          <Form.Item label={t("domains.Wallet.address")} name="address">
            <Input readOnly />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.balanceMax")}
            name="balance_max"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.balanceMax"),
                }),
              },
            ]}
            extra={t("systems.Extra.noLimitIfZero")}
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.balanceMax"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.dailyDepositMax")}
            name="daily_deposit_max"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.dailyDepositMax"),
                }),
              },
            ]}
            extra={t("systems.Extra.noLimitIfZero")}
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.dailyDepositMax"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.weeklyDepositMax")}
            name="weekly_deposit_max"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.weeklyDepositMax"),
                }),
              },
            ]}
            extra={t("systems.Extra.noLimitIfZero")}
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.weeklyDepositMax"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.monthlyDepositMax")}
            name="monthly_deposit_max"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.monthlyDepositMax"),
                }),
              },
            ]}
            extra={t("systems.Extra.noLimitIfZero")}
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.monthlyDepositMax"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Wallet.note")}
            name="note"
            rules={[
              {
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Wallet.note"),
                }),
              },
            ]}
          >
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Wallet.note"),
              })}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  }
);
